/**
 * ListViewControls - Toolbar above the list detail views
 */
import type { ReactElement } from 'react';
import { useState } from 'react';

import { useAtom } from 'jotai';
import { FaFlag, FaTimes } from 'react-icons/fa';

import AvailabilityFilter from '@/components/AvailabilityFilter/AvailabilityFilter';
import Button from '@/components/Button/Button';
import type { FilterDropdownOption } from '@/components/FilterDropdown/FilterDropdown';
import FilterDropdown from '@/components/FilterDropdown/FilterDropdown';
import GroupBySelector from '@/components/GroupBySelector/GroupBySelector';
import Search from '@/components/Search/Search';
import SortBySelector from '@/components/SortBySelector/SortBySelector';
import ViewControlsBar from '@/components/ViewControlsBar/ViewControlsBar';
import { PRIORITY_LABELS, type ListPriority } from '@/types/lists';

import { LIST_PRIORITY_BADGE, LIST_PRIORITY_ORDER, LIST_PRIORITY_TEXT } from '../lib/listPriority';
import {
  LIST_GROUP_BY_OPTIONS,
  LIST_SORT_OPTIONS,
  listAvailabilityFilterAtom,
  listGroupByAtom,
  listPriorityFilterAtom,
  listSearchQueryAtom,
  listSortAtom,
  listViewModeAtom,
} from '../state/listViewSettings';
import ListColumnVisibilityDropdown from './ListColumnVisibilityDropdown';
import ListViewToggle from './ListViewToggle';

const PRIORITY_OPTIONS: FilterDropdownOption[] = LIST_PRIORITY_ORDER.map((value) => ({
  value,
  label: PRIORITY_LABELS[value],
  icon: <FaFlag className={`h-3 w-3 ${LIST_PRIORITY_TEXT[value]}`} aria-hidden="true" />,
  badgeClassName: LIST_PRIORITY_BADGE[value],
}));

const PRIORITY_TRIGGER_ICON = <FaFlag className="h-3 w-3" aria-hidden="true" />;

export default function ListViewControls(): ReactElement {
  const [search, setSearch] = useAtom(listSearchQueryAtom);
  const [availability, setAvailability] = useAtom(listAvailabilityFilterAtom);
  const [priorities, setPriorities] = useAtom(listPriorityFilterAtom);
  const [groupBy, setGroupBy] = useAtom(listGroupByAtom);
  const [sort, setSort] = useAtom(listSortAtom);
  const [viewMode, setViewMode] = useAtom(listViewModeAtom);

  // Typed text stays local; only the debounced value reaches the atom, so the card views
  // re-render once per pause instead of once per keystroke.
  const [searchText, setSearchText] = useState(search);

  const hasActiveFilters = search.trim() !== '' || availability !== 'all' || priorities.length > 0;

  const clearFilters = () => {
    setSearchText('');
    setSearch('');
    setAvailability('all');
    setPriorities([]);
  };

  return (
    <ViewControlsBar
      left={
        <>
          <Search
            value={searchText}
            onChangeImmediate={setSearchText}
            onSearch={setSearch}
            placeholder="Search this list…"
            ariaLabel="Search cards in list"
            className="w-56"
          />
          <AvailabilityFilter value={availability} onChange={setAvailability} />
          <FilterDropdown
            triggerLabel="Priority"
            triggerIcon={PRIORITY_TRIGGER_ICON}
            ariaLabel="Filter by priority"
            searchPlaceholder="Search priorities..."
            options={PRIORITY_OPTIONS}
            selectedValues={priorities}
            onToggle={(value) =>
              setPriorities((prev) =>
                prev.includes(value as ListPriority) ? prev.filter((p) => p !== value) : [...prev, value as ListPriority],
              )
            }
            onClear={() => setPriorities([])}
          />
          {hasActiveFilters && (
            <Button icon={<FaTimes />} text="Clear filters" variant="text" color="secondary" size="small" onClick={clearFilters} />
          )}
        </>
      }
      right={
        <>
          {viewMode === 'table' && <ListColumnVisibilityDropdown />}
          <GroupBySelector value={groupBy} onChange={setGroupBy} options={LIST_GROUP_BY_OPTIONS} />
          <SortBySelector
            value={sort.field}
            direction={sort.direction}
            options={LIST_SORT_OPTIONS}
            onChange={(field, direction) => setSort({ field, direction })}
            variant="dropdown"
          />
          <ListViewToggle value={viewMode} onChange={setViewMode} />
        </>
      }
    />
  );
}
